<template>
  <div class="customer__list">
    <div class="search">
      <SearchBar
        class="search-bar"
        placeholder="顧客名、電話番号、会社名"
        v-model="_searchWord"
        :disabled="isFromReservationList"
        @keydown.enter="handleSearchKeydown"
        @compositionstart="isComposing = true"
        @compositionend="isComposing = false"
        @search="search"
      />
      <a
        href="#"
        class="search-detail-btn"
        :class="{ on: isConditionEnable, disabled: isFromReservationList }"
        @click.prevent="showConditionModal"
        ><span>詳細</span></a
      >
    </div>
    <div class="customer__feed">
      <DynamicScroller
        v-if="!isFromReservationList"
        class="scroller"
        :class="{ nodata: isNoData }"
        :items="customerList"
        :min-item-size="12"
        v-scroll-over="next"
      >
        <template #before>
          <div v-show="isNoData" class="nodata-text">
            <CustomerIcon width="64" height="64" />
            <p>顧客情報がありません。</p>
          </div>
        </template>
        <template v-if="isActive" v-slot="{ item, index, active }">
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[item.attributes]"
            emitResize
          >
            <div v-if="isVisibleIndex(index)" class="index">
              <span>{{ item.index }}</span>
            </div>
            <div
              class="item"
              :class="{ selected: selectedCustomerId === item.id }"
              @click.prevent="selectCustomer(item.id)"
            >
              <div class="cstm-head">
                <p class="cstm-name">
                  {{ toEllipsis(item.name, 19) }}
                  <span>様</span>
                </p>
                <div class="params">
                  <MemberLabel v-if="!!item.syncMemberType" />
                  <VisitCount :visitCount="item.visitCount" />
                </div>
              </div>
              <p class="cstm-option">{{ getSupplementary(item.supplementary) }}</p>
              <CustomerLabels class="labels" :good="item.attributes.normal" :bad="item.attributes.bad" />
            </div>
            <div v-if="!isLastItem(index)" class="separator" />
          </DynamicScrollerItem>
        </template>
        <template #after>
          <div v-if="isGetCustomerByConditionBusy" class="customer__loader">
            <LoadingIndicator small />
          </div>
        </template>
      </DynamicScroller>
    </div>
  </div>
</template>

<script>
import { toEllipsis } from '@/utils/string'
import { formatDate } from '@/utils/date'
import ScrollOver from '@/directives/ScrollOver'
import MemberLabel from '@/components/atoms/MemberLabel'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import { DynamicScroller, DynamicScrollerItem } from '@/components/molecules/VirtualScroller'
import CustomerIcon from '@/components/icons/customer'
import CustomerLabels from '@/components/molecules/CustomerLabels'
import SearchBar from '@/components/molecules/SearchBar'
import VisitCount from '@/components/molecules/VisitCount'

/**
 * 顧客リスト
 */
export default {
  name: 'CustomerList',
  components: {
    MemberLabel,
    LoadingIndicator,
    DynamicScroller,
    DynamicScrollerItem,
    CustomerIcon,
    CustomerLabels,
    SearchBar,
    VisitCount,
  },
  directives: {
    ScrollOver,
  },
  props: {
    searchWord: String,
    isConditionEnable: Boolean,
    supplementaryType: Number,
    customerList: Array,
    isGetCustomerByConditionBusy: Boolean,
    isGetCustomerByConditionEnd: Boolean,
    selectedCustomerId: Number,
    isFromReservationList: Boolean,
    heights: Array,
  },
  data() {
    return {
      isComposing: false,
    }
  },
  computed: {
    _searchWord: {
      get() {
        return this.searchWord
      },
      set(value) {
        this.$emit('updateSearchWord', value)
      },
    },
    isGetCustomerByConditionInfinityDisabled() {
      return this.isGetCustomerByConditionBusy || this.isGetCustomerByConditionEnd
    },
    isNoData() {
      return this.isGetCustomerByConditionEnd && !this.customerList.length
    },
    isActive() {
      return this.customerList.length > 0
    },
  },
  methods: {
    toEllipsis,
    showConditionModal() {
      this.$emit('showConditionModal')
    },
    getSupplementary(supplementary) {
      if (this.supplementaryType === 2) {
        const lastVisitDate = supplementary.match(/(\d{4}-\d{2}-\d{2})/)
        return `最終来店：${lastVisitDate ? formatDate(lastVisitDate[0], 'YYYY/M/D') : ''}`
      }
      return supplementary
    },
    handleSearchKeydown() {
      if (!this.isComposing) {
        this.search()
      }
    },
    search() {
      this.$emit('search')
    },
    next() {
      if (!this.isGetCustomerByConditionInfinityDisabled) this.$emit('next')
    },
    selectCustomer(id) {
      this.$emit('selectCustomer', id)
    },
    isVisibleIndex(index) {
      return index === 0 || this.customerList[index]?.index !== this.customerList[index - 1]?.index
    },
    isLastItem(index) {
      return this.customerList[index]?.index !== this.customerList[index + 1]?.index
    },
  },
}
</script>

<style lang="postcss" scoped>
.customer__list {
  display: grid;
  grid-template-rows: max-content 1fr;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  transition: all 0.3s ease;
}

.nodata {
  position: relative;

  .nodata-text {
    align-items: center;
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 31%;
    width: 100%;

    >>> p {
      color: var(--text-color);
    }

    >>> svg {
      fill: var(--light-gray);
    }
  }
}

.search {
  background-color: var(--white);
  border-bottom: 1px solid var(--border-color);
  border-right: 1px solid var(--border-color);
  box-sizing: border-box;
  display: grid;
  flex-shrink: 0;
  grid-gap: 8px;
  grid-template-columns: 1fr auto;
  height: 56px;
  padding: 8px;
}

.search-bar {
  border-color: var(--dark-gray);
  width: 100%;
}

.params {
  display: grid;
  grid-auto-flow: column;
  justify-self: flex-end;
  padding-left: 8px;
}

.cstm-head {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
}

.cstm-name {
  font-weight: bold;
  line-height: 1;

  > span {
    font-size: 13px;
    font-weight: normal;
  }
}

.search-detail-btn {
  border: 1px solid var(--dark-gray);
  border-radius: 4px;
  box-sizing: border-box;
  display: block;
  height: 40px;
  width: 56px;

  span {
    background: url(/assets/img/icons/search_detail.png) no-repeat center center var(--very-light-gray-3);
    background-size: 18px 12px;
    border-radius: 2px;
    display: block;
    height: 34px;
    margin: 2px;
    text-indent: -9999px;
  }

  &.on span {
    background-color: var(--very-light-yellow);
  }

  &.disabled {
    border: 1px solid var(--border-color);
    pointer-events: none;

    span {
      background: url(/assets/img/icons/search_detail_disabled.png) no-repeat center center var(--light-gray);
      background-size: 18px 12px;
      height: 100%;
      margin: 0;
    }
  }
}

.customer__feed {
  border-right: 1px solid var(--border-color);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  -webkit-transform: translateZ(0);
  -webkit-transform: translate3d(0, 0, 0);

  .scroller {
    -webkit-overflow-scrolling: touch;
    -webkit-transform: translateZ(0);
    -webkit-transform: translate3d(0, 0, 0);
  }

  .index {
    background-color: var(--very-light-gray-2);
    box-sizing: border-box;
    height: 40px;
    margin-top: -1px;
    padding-left: 16px;

    span {
      font-size: 21px;
      line-height: 1.9;
    }
  }

  .separator {
    background-color: var(--border-color);
    content: '';
    display: block;
    height: 1px;
    margin: -1px 24px 0;
  }

  .item {
    background-color: var(--white);
    box-sizing: border-box;
    cursor: pointer;
    display: block;
    min-height: 96px;
    padding: 16px 24px;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

    &.selected {
      background-color: var(--very-light-yellow);
    }
  }

  .labels {
    justify-content: flex-end;
    padding-top: 11px;
  }

  .responsive & {
    @media screen and (max-width: 639px) {
      .item {
        padding: 16px;
      }

      .separator {
        margin: -1px 0 0;
      }
    }
  }
}

.cstm-option {
  color: var(--very-dark-gray-2);
  font-size: 13px;
  line-height: 1;
  margin-top: 8px;
}
</style>
